// Headless render layer for the Glance digest card: the triage-based digest, download onboarding,
// error, and caught-up states. Data/transport side effects live in `useGlance` + `useTriageDriver`; this
// file additionally owns the focus-management effects (focus into the card on open, restore to the
// opener on close/unmount).

import { CheckCircle, Stars01, XClose } from "@untitledui/icons";
import { useEffect, useRef, useState } from "react";
import { Button } from "../Button";
import { Icon } from "../Icon";
import { IconButton } from "../IconButton";
import { Surface } from "../Surface";
import { DigestCard } from "./DigestCard";
import styles from "./Glance.module.css";
import { type UseGlanceOptions, useGlance } from "./useGlance";

export interface GlanceLabels {
  /** Stable, visible title atop the card. */
  title: string;
  /** App-name shown in the notification-style header — the Reflecto brand. */
  appName: string;
  /** Accessible label for the close (X) IconButton. */
  closeLabel: string;
  /** Chip next to the title while the AI digest is surfaced. */
  aiSummaryTag: string;
  /** Fixed copy for the error view: a component `fail()`, or a mid-session availability regression
   * (model became unsupported/unavailable while the card was open). */
  errorAi: string;
  /** Retry button label shown in the `error` state. */
  retryCta: string;
  /** Onboarding heading shown when the model is downloadable ("Get AI summaries"). */
  downloadHeading: string;
  /** Onboarding body — what the recap does + reassurance it's on-device. */
  downloadBody: string;
  /** Download CTA button label. */
  downloadCta: string;
  /** Size / privacy note beneath the Download CTA. */
  downloadPrivacyNote: string;
  /** Heading shown above the download progress bar while the model downloads. */
  downloadingHeading: string;
  /** Warm "all caught up" line shown when the 24h window finds nothing recent. */
  caughtUp: string;
  /** Section heading for the needs-you tier. */
  needsYouLabel: string;
  /** Section heading for the quiet tier. */
  quietLabel: string;
  /** Full scanning-status line for the cold-cache state, taking the number of
   *  items still pending (e.g. "Summarizing 6…") — a function so verb-final
   *  locales control the word order around the count. */
  scanningLabel: (pending: number) => string;
  /** Accessible label for a row's jump-to-notification button. */
  jumpLabel: string;
  /** CTA text for the reply affordance. */
  affordanceReply: string;
  /** CTA text for the open affordance. */
  affordanceOpen: string;
  /** CTA text for the copy-OTP affordance. */
  affordanceCopyOtp: string;
}

export interface GlanceProps extends UseGlanceOptions {
  labels: GlanceLabels;
  /** Strictly-increasing header counter; each new value surfaces the card (a repeat, incl. the 0 sentinel, is a no-op). */
  trigger: number;
  /** Starts the model download from the needs-download card — consumed by the component, not the hook. */
  startDownload: () => Promise<void>;
  /** Last download/probe error from the AI-status orchestrator. Surfaced on the needs-download card so a
   * failed download isn't a silent revert-to-prompt (onDownload handles errors internally, never rejects). */
  downloadError?: string;
  /** Per-app icons (package → base64 PNG), host-supplied from storage; mirrors the notification list. */
  appIconCache: Record<string, string>;
  /** Optional jump seam — the `›` link renders only when a host wires this (keeps the headless layer transport-free). */
  onJumpTo?: (target: { msgId: string } | { conversationKey: string }) => void;
}

// Download-in-flight state: a progress bar plus a visible numeric percentage. magnitude is NOT
// conveyed by bar-fill alone; low-vision / screen-reader users get `aria-valuetext` + the visible %.
// role="status" so the transition off the needs-download onboarding card is announced.
function DownloadingState(props: { loaded: number; total?: number; label: string }) {
  const { loaded, total, label } = props;
  // Determinate only with a non-zero loaded AND total; else indeterminate (a bare `value` without `max`
  // clamps to 100%, misleading mid-download). The guard also narrows `total`, so the division needs no cast.
  const pct =
    loaded > 0 && total !== undefined && total > 0 ? Math.round((loaded / total) * 100) : null;
  const determinate = pct !== null;
  return (
    <div className={styles.downloadingState} role="status">
      <progress
        className={styles.progress}
        value={determinate ? loaded : undefined}
        max={total}
        aria-label={label}
        aria-valuetext={pct !== null ? `${pct}%` : undefined}
      />
      {pct !== null ? <span className={styles.progressPercent}>{pct}%</span> : null}
    </div>
  );
}

// Header-driven, pure-AI, read-only digest card. A header `trigger` counter surfaces the card;
// the card switches on the hook's single `view.kind` for every state.
export function Glance(props: GlanceProps) {
  const { labels, trigger, startDownload, downloadError, appIconCache, onJumpTo, ...hookOpts } =
    props;
  const result = useGlance(hookOpts);

  // downloadError (aiStatus.errorMessage) outlives a close — it's owned by the host's useAIStatus, which
  // close() can't reset. Gate the alert on a local flag: shown only after a download attempt this open,
  // cleared on close, so a re-opened needs-download card never replays a stale failure.
  const [errorVisible, setErrorVisible] = useState(false);
  const { view } = result;

  // Surface once per trigger bump. A 0 trigger is the "never clicked" sentinel. Surfacing and
  // closing is entirely the hook's `view` — the component owns no surfaced state.
  const ranForTrigger = useRef(0);
  useEffect(() => {
    if (trigger > 0 && ranForTrigger.current !== trigger) {
      ranForTrigger.current = trigger;
      result.surface();
    }
  }, [trigger, result.surface]);

  // Focus management for the dynamically surfaced region: remember the opener, move focus into the card on
  // surface, restore it on close — the trigger lives in the host header, so otherwise focus orphans to <body>.
  const cardRef = useRef<HTMLElement>(null);
  const returnFocusRef = useRef<HTMLElement | null>(null);
  const wasOpen = useRef(false);
  const isOpen = view.kind !== "idle";
  useEffect(() => {
    if (isOpen && !wasOpen.current) {
      returnFocusRef.current = document.activeElement as HTMLElement | null;
      cardRef.current?.focus();
    } else if (!isOpen && wasOpen.current) {
      // Only refocus if the opener is still in the DOM — it may have unmounted (e.g. the AI header
      // button), and .focus() on a detached node orphans focus to <body> (WCAG 2.4.3).
      const el = returnFocusRef.current;
      if (el?.isConnected) el.focus();
      returnFocusRef.current = null;
    }
    wasOpen.current = isOpen;
  }, [isOpen]);

  // Unmount-while-open (host closes the panel) skips the [isOpen] close branch above — return focus on
  // teardown so it isn't orphaned to <body>. Empty deps ⇒ this cleanup runs only on unmount.
  useEffect(() => {
    return () => {
      const el = returnFocusRef.current;
      if (wasOpen.current && el?.isConnected) el.focus();
      // Reset so the [isOpen] open branch can re-fire if React remounts us (StrictMode double-mount):
      // a stale `true` would skip the open branch and strand focus on the trigger.
      wasOpen.current = false;
    };
  }, []);

  if (view.kind === "idle") return null;

  const titleText =
    view.kind === "needs-download"
      ? labels.downloadHeading
      : view.kind === "downloading"
        ? labels.downloadingHeading
        : labels.title;

  const titleNode = <h3 className={styles.glanceTitle}>{titleText}</h3>;

  return (
    <Surface tone="secondary" className={styles.card}>
      {/* the focusable landmark (focused on surface; see the effect above). A named `<section>`
          is a region landmark; the STABLE `aria-label` (NOT aria-labelledby to a streaming node) keeps
          the accessible name from churning; `tabIndex={-1}` lets us move focus here
          programmatically without adding it to the tab order. */}
      <section ref={cardRef} aria-label={labels.title} tabIndex={-1} className={styles.region}>
        <div className={styles.cardHeader}>
          <div className={styles.headerMeta}>
            <Icon as={Stars01} size="sm" className={styles.brandIcon} />
            <span className={styles.appName}>{labels.appName}</span>
            {/* AI tag always shows once surfaced — the card is the on-device-AI surface in every state. */}
            <span className={styles.aiTag}>{labels.aiSummaryTag}</span>
          </div>
          <IconButton
            icon={XClose}
            variant="default"
            size="s"
            label={labels.closeLabel}
            onClick={() => {
              setErrorVisible(false);
              result.close();
            }}
          />
        </div>

        {titleNode}

        {view.kind === "digest" ? (
          // aria-busy while the lead streams: suppresses per-chunk SR announcements until it settles.
          <div className={styles.narration} aria-live="polite" aria-busy={view.leadStreaming}>
            <DigestCard
              digest={view.model}
              appIconCache={appIconCache}
              labels={{
                needsYouLabel: labels.needsYouLabel,
                quietLabel: labels.quietLabel,
                scanningLabel: labels.scanningLabel,
                jumpLabel: labels.jumpLabel,
                affordance: {
                  reply: labels.affordanceReply,
                  open: labels.affordanceOpen,
                  copy_otp: labels.affordanceCopyOtp,
                  none: "",
                },
              }}
              onJumpTo={onJumpTo}
            />
          </div>
        ) : null}

        {view.kind === "needs-download" ? (
          // role="status" so this onboarding card is announced when it appears
          // (otherwise the SR is left with a stale state).
          <div className={styles.onboarding} role="status">
            <p className={styles.body}>{labels.downloadBody}</p>
            {/* Shown only after a download attempt this open (errorVisible) so a re-opened card doesn't
                replay a stale failure. role="alert" announces it; the Download button below is the retry. */}
            {errorVisible && downloadError ? (
              <p role="alert" className={styles.errorNote}>
                {downloadError}
              </p>
            ) : null}
            <Button
              variant="primary"
              size="s"
              // .catch(fail) is defensive — onDownload swallows errors internally today, but a future
              // rejecting impl must not become a silent unhandled rejection (typescript.md).
              onClick={() => {
                setErrorVisible(true);
                startDownload().catch(() => result.fail());
              }}
            >
              {labels.downloadCta}
            </Button>
            <p className={styles.privacyNote}>{labels.downloadPrivacyNote}</p>
          </div>
        ) : null}

        {view.kind === "downloading" ? (
          <DownloadingState
            loaded={view.loaded}
            total={view.total}
            label={labels.downloadingHeading}
          />
        ) : null}

        {view.kind === "error" ? (
          // Fixed "ai" copy in role="alert". Retry shows only when canRetry: a transient component fail()
          // (surface() re-runs the pass). A mid-session availability regression sets canRetry:false (not
          // recoverable here) and hides Retry — the close X is the way out.
          <div className={styles.errorState}>
            <p role="alert" className={styles.errorNote}>
              {labels.errorAi}
            </p>
            {view.canRetry ? (
              <Button variant="primary" size="s" onClick={() => result.surface()}>
                {labels.retryCta}
              </Button>
            ) : null}
          </div>
        ) : null}

        {view.kind === "caught-up" ? (
          <div className={styles.caughtUp} role="status">
            <Icon as={CheckCircle} size="md" className={styles.caughtUpIcon} />
            <p className={styles.caughtUpText}>{labels.caughtUp}</p>
          </div>
        ) : null}
      </section>
    </Surface>
  );
}
